import useCreateAddress from '@/api/mutations/addresses/useCreateAddress';
import useTransferStock from '@/api/mutations/stockLocations/useTransferStock';
import useGetProjectMaterialsListSummaryItems, {
  MaterialsListMaterial,
} from '@/api/queries/project/useGetProjectMaterialListSummaryItems';
import useGetProjectStockSources, {
  ProjectStockSource,
} from '@/api/queries/project/useGetProjectStockSources';
import MaterialsLeftToOrderList from '@/ui/components/createPurchaseOrderSidebar/components/MaterialsLeftToOrderList';
import MaterialsToOrderList from '@/ui/components/createPurchaseOrderSidebar/components/MaterialsToOrderList';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Conditional,
  Sidebar,
  SidebarProps,
  SkeletonLoader,
  useToastContext,
} from '@payaca/ambrosia';

import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';
import { useDeal } from '@payaca/store/hooks/appState';
import { requestPersistPurchaseOrder } from '@payaca/store/materialsList/materialsListActions';
import { PersistPurchaseOrderRequestData } from '@payaca/store/materialsList/materialsListTypes';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, useMemo, useReducer, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { z } from 'zod/v4';
import useSubmitDirectPurchaseOrderCityPlumbing from '../../../api/mutations/purchaseOrders/useSubmitDirectPurchaseOrderCityPlumbing';
import { SubmitDirectPurchaseOrderCityPlumbingInput } from '../../../gql/graphql';
import DirectPurchaseOrderSummary from './components/DirectPurchaseOrderSummary';
import FulfillmentOptions from './components/FulfillmentOptions';
import IndirectPurchaseOrderDetails from './components/IndirectPurchaseOrderDetails';

const BASE_PURCHASE_ORDER_SCHEMA = z.object({
  supplierId: z.number(),
  tagIds: z.array(z.number()).optional(),
  materials: z.array(z.any()),
  additionalNotes: z.string().optional(),
});
const INDIRECT_PURCHASE_ORDER_SCHEMA = BASE_PURCHASE_ORDER_SCHEMA.extend({
  type: z.literal('indirect'),
  deliveryInfo: z.object({
    deliveryAddressId: z.number().optional(),
    newDeliveryAddress: z
      .object({
        line1: z.string().optional(),
        line2: z.string().optional(),
        city: z.string().optional(),
        administrativeArea: z.string().optional(),
        postcode: z.string().optional(),
        country: z.string().optional(),
      })
      .optional(),
  }),
  dueAt: z.date().optional(),
  showMaterialPrices: z.boolean().optional(),
});
const BASE_DIRECT_PURCHASE_ORDER_SCHEMA = BASE_PURCHASE_ORDER_SCHEMA.extend({
  userContactId: z.string(),
  delivery: z
    .union([
      z.object({
        deliveryAddressId: z.number(),
        newDeliveryAddress: z.undefined(),
        branchId: z.string().optional(),
        fullLocalAddress: z.string().optional(),
        branchName: z.string().optional(),
      }),
      z.object({
        deliveryAddressId: z.undefined(),
        newDeliveryAddress: z.object({
          line1: z.string().optional(),
          line2: z.string().optional(),
          city: z.string().optional(),
          administrativeArea: z.string().optional(),
          postcode: z.string().optional(),
          country: z.string().optional(),
        }),
        branchId: z.string().optional(),
        fullLocalAddress: z.string().optional(),
        branchName: z.string().optional(),
      }),
    ])
    .optional(),
  collection: z
    .object({
      branchId: z.string(),
      branchName: z.string().optional(),
    })
    .optional(),
});
const DIRECT_PURCHASE_ORDER_ASAP_SCHEMA =
  BASE_DIRECT_PURCHASE_ORDER_SCHEMA.extend({
    type: z.literal('direct-asap'),
    fulfillmentOptionId: z.string().optional(),
    fromDate: z.date().optional(),
    toDate: z.date().optional(),
  });
const DIRECT_PURCHASE_ORDER_ACO_SCHEMA =
  BASE_DIRECT_PURCHASE_ORDER_SCHEMA.extend({
    type: z.literal('direct-aco'),
    callOffDate: z.date().optional(),
  });
// Sourcing from stock rather than a supplier: no supplierId, no delivery or
// fulfilment, just the source and destination Lists and the materials to move.
// Deliberately does not extend the supplier base schema, so the supplier
// variants stay untouched.
const STOCK_TRANSFER_SCHEMA = z.object({
  type: z.literal('stock-transfer'),
  fromMaterialsListId: z.string(),
  toMaterialsListId: z.string(),
  materials: z.array(z.any()),
});
const PURCHASE_ORDER_FORM_SCHEMA = z.discriminatedUnion('type', [
  INDIRECT_PURCHASE_ORDER_SCHEMA,
  DIRECT_PURCHASE_ORDER_ASAP_SCHEMA,
  DIRECT_PURCHASE_ORDER_ACO_SCHEMA,
  STOCK_TRANSFER_SCHEMA,
]);

export type PurchaseOrderMaterial = MaterialsListMaterial & {
  selectedQuantity: number;
  purchaseOrderQuantity: number;
  // For a stock transfer, the movable cap (min of on-hand and still-required);
  // undefined for a supplier order, where the still-required quantity is the cap.
  quantityCap?: number;
};

// Distributive Omit preserves discriminated union variants
type WithMaterials<T> = T extends any
  ? Omit<T, 'materials'> & { materials: PurchaseOrderMaterial[] }
  : never;

export type TFormState = WithMaterials<
  z.infer<typeof PURCHASE_ORDER_FORM_SCHEMA>
>;

type FormStageState = {
  formStage: FormStageStage;
  transitionComplete: boolean;
};
export type FormStageStage =
  | 'MATERIAL_LIST_MATERIALS' // materials left to order / materials to order
  | 'FULFILLMENT_OPTIONS' // materials to order / fulfillment options
  | 'DIRECT_PURCHASE_ORDER' // fulfillment options / direct purchase order summary
  | 'INDIRECT_PURCHASE_ORDER_DETAILS'; // materials to order / purchase order details

type FormStageAction =
  | { type: 'GO_TO_MATERIAL_LIST_MATERIALS_STAGE' }
  | { type: 'GO_TO_FULFILLMENT_OPTIONS_STAGE' }
  | { type: 'GO_TO_DIRECT_PURCHASE_ORDER_STAGE' }
  | { type: 'GO_TO_INDIRECT_PURCHASE_ORDER_DETAILS_STAGE' }
  | { type: 'TRANSITION_COMPLETE' }
  | { type: 'RESET' };

const formStageReducer = (
  state: FormStageState,
  action: FormStageAction
): FormStageState => {
  switch (action.type) {
    case 'GO_TO_MATERIAL_LIST_MATERIALS_STAGE':
      return {
        formStage: 'MATERIAL_LIST_MATERIALS',
        transitionComplete: false,
      };
    case 'GO_TO_FULFILLMENT_OPTIONS_STAGE':
      return {
        formStage: 'FULFILLMENT_OPTIONS',
        transitionComplete: false,
      };

    case 'GO_TO_DIRECT_PURCHASE_ORDER_STAGE':
      return { formStage: 'DIRECT_PURCHASE_ORDER', transitionComplete: false };
    case 'GO_TO_INDIRECT_PURCHASE_ORDER_DETAILS_STAGE':
      return {
        formStage: 'INDIRECT_PURCHASE_ORDER_DETAILS',
        transitionComplete: false,
      };
    case 'TRANSITION_COMPLETE':
      return { ...state, transitionComplete: true };
    case 'RESET':
      return defaultFormStageState;
    default:
      return state;
  }
};

const defaultFormStageState: FormStageState = {
  formStage: 'MATERIAL_LIST_MATERIALS',
  transitionComplete: true,
};

const CreatePurchaseOrderForm: FC<{
  projectId: number;
  materialListMaterials: MaterialsListMaterial[];
  stockSources: ProjectStockSource[];
  destinationMaterialsListPublicId?: string;
  onSubmit: (state: TFormState) => Promise<void>;
}> = (props) => {
  const {
    projectId,
    materialListMaterials,
    stockSources,
    destinationMaterialsListPublicId,
    onSubmit,
  } = props;

  const [formStageState, formStageDispatch] = useReducer(
    formStageReducer,
    defaultFormStageState
  );

  const [isSaving, setIsSaving] = useState(false);

  const formMethods = useForm<TFormState>({
    resolver: zodResolver(PURCHASE_ORDER_FORM_SCHEMA),
    defaultValues: {
      materials: [],
      type: 'indirect',
      deliveryInfo: {},
    },
  });
  const selectedMaterials = formMethods.watch('materials');
  const formType = formMethods.watch('type');
  const isStockTransfer = formType === 'stock-transfer';

  const handleSubmit = async (state: TFormState) => {
    setIsSaving(true);

    await onSubmit(state);

    setIsSaving(false);
  };

  const showDirectPurchaseOrderFlow = ['direct-aco', 'direct-asap'].includes(
    formType
  );

  const formClassName = useMemo(() => {
    // A stock transfer is a single stage with no fulfilment or delivery step:
    // the two material columns sit side by side with no sliding transition.
    if (isStockTransfer) {
      return 'absolute top-0 left-0 w-full h-full grid grid-cols-2';
    }

    const isFinalStage =
      formStageState.formStage === 'INDIRECT_PURCHASE_ORDER_DETAILS' ||
      formStageState.formStage === 'DIRECT_PURCHASE_ORDER';

    if (showDirectPurchaseOrderFlow) {
      let translateClassName = 'translate-x-0';
      if (isFinalStage) {
        translateClassName = '-translate-x-2/4';
      } else if (formStageState.formStage === 'FULFILLMENT_OPTIONS') {
        translateClassName = '-translate-x-1/4';
      }
      return clstx(
        // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Percentage size has no utility
        'absolute top-0 left-0 w-[200%] h-full grid grid-cols-4 transition-transform duration-500',
        translateClassName
      );
    } else {
      return clstx(
        // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Percentage size has no utility
        'absolute top-0 left-0 w-[150%] h-full grid grid-cols-3 transition-transform duration-500',
        isFinalStage ? '-translate-x-1/3' : 'translate-x-0'
      );
    }
  }, [isStockTransfer, showDirectPurchaseOrderFlow, formStageState.formStage]);

  return (
    <FormProvider {...formMethods}>
      <form
        className={formClassName}
        onTransitionEnd={() => {
          formStageDispatch({ type: 'TRANSITION_COMPLETE' });
        }}
        onSubmit={formMethods.handleSubmit(handleSubmit, console.error)}
      >
        <MaterialsLeftToOrderList
          materialListMaterials={materialListMaterials}
          hideList={
            (formStageState.formStage === 'INDIRECT_PURCHASE_ORDER_DETAILS' ||
              formStageState.formStage === 'FULFILLMENT_OPTIONS') &&
            formStageState.transitionComplete
          }
        />

        <MaterialsToOrderList
          materialListMaterials={materialListMaterials}
          stockSources={stockSources}
          destinationMaterialsListPublicId={destinationMaterialsListPublicId}
          formStage={formStageState.formStage}
          hideList={
            showDirectPurchaseOrderFlow
              ? formStageState.formStage ===
                  'INDIRECT_PURCHASE_ORDER_DETAILS' &&
                formStageState.transitionComplete
              : false
          }
          onAction={() => {
            if (formStageState.formStage === 'MATERIAL_LIST_MATERIALS') {
              if (showDirectPurchaseOrderFlow) {
                formStageDispatch({
                  type: 'GO_TO_FULFILLMENT_OPTIONS_STAGE',
                });
              } else {
                formStageDispatch({
                  type: 'GO_TO_INDIRECT_PURCHASE_ORDER_DETAILS_STAGE',
                });
              }
            } else {
              // reset materials purchase order quantities
              formMethods.setValue(
                'materials',
                selectedMaterials.map((m) => ({
                  ...m,
                  purchaseOrderQuantity: m.selectedQuantity,
                }))
              );
              // clear direct PO fields
              formMethods.resetField('delivery');
              formMethods.resetField('collection');
              formMethods.resetField('fulfillmentOptionId');
              formMethods.resetField('fromDate');
              formMethods.resetField('toDate');
              formMethods.resetField('userContactId');

              // clear non-direct PO fields
              formMethods.resetField('deliveryInfo');
              formMethods.resetField('dueAt');
              formMethods.resetField('additionalNotes');
              formMethods.resetField('showMaterialPrices');

              formStageDispatch({
                type: 'GO_TO_MATERIAL_LIST_MATERIALS_STAGE',
              });
            }
          }}
          actionDisabled={isSaving}
        />
        <Conditional
          condition={showDirectPurchaseOrderFlow && !isStockTransfer}
        >
          <FulfillmentOptions
            projectId={projectId}
            formStage={formStageState.formStage}
            onAction={() => {
              if (formStageState.formStage === 'FULFILLMENT_OPTIONS') {
                formStageDispatch({
                  type: 'GO_TO_DIRECT_PURCHASE_ORDER_STAGE',
                });
              } else {
                // clear direct PO fields
                formMethods.resetField('delivery');
                formMethods.resetField('collection');
                formMethods.resetField('fulfillmentOptionId');
                formMethods.resetField('fromDate');
                formMethods.resetField('toDate');
                formMethods.resetField('callOffDate');
                formMethods.setValue('type', 'direct-asap');

                // revert materials to original selected state
                formMethods.setValue(
                  'materials',
                  selectedMaterials.map((m) => ({
                    ...m,
                    purchaseOrderQuantity: m.selectedQuantity,
                  }))
                );
                formStageDispatch({
                  type: 'GO_TO_FULFILLMENT_OPTIONS_STAGE',
                });
              }
            }}
          />
          <DirectPurchaseOrderSummary
            formStage={formStageState.formStage}
            isSaving={isSaving}
          />
        </Conditional>
        <Conditional
          condition={!showDirectPurchaseOrderFlow && !isStockTransfer}
        >
          <IndirectPurchaseOrderDetails
            formStage={formStageState.formStage}
            projectId={projectId}
            hideList={
              formStageState.formStage === 'MATERIAL_LIST_MATERIALS' &&
              formStageState.transitionComplete
            }
            isSaving={isSaving}
          />
        </Conditional>
      </form>
    </FormProvider>
  );
};

export interface IProps extends SidebarProps {
  projectId: number;
  materialsListId: number;
  /** Called with the new purchase order's id when a supplier order is raised. */
  onPurchaseOrderSuccess?: (purchaseOrderId: number) => void;
  /**
   * Called after stock is moved into the project from a stock location. A
   * transfer has no purchase order to navigate to, so this just lets the caller
   * close the sidebar and refresh the Materials tab.
   */
  onTransferSuccess?: () => void;
}

const CreatePurchaseOrderSidebar: FC<IProps> = (props) => {
  const {
    isOpen,
    projectId,
    materialsListId,
    onPurchaseOrderSuccess,
    onTransferSuccess,
    ...rest
  } = props;

  const { pushError } = useToastContext();

  /**
   * Redux
   */
  const reduxDispatch = useDispatch();
  const deal = useDeal(projectId);

  /**
   * Queries
   */
  const {
    materialsListOrderSummaryItems: materialsYetToOrder,
    data: summaryItemsData,
    isLoading,
  } = useGetProjectMaterialsListSummaryItems(
    deal?.publicId,
    {
      statuses: ['TO_ORDER'],
    },
    {
      ...PAGINATION_ARG_DEFAULTS,
      limit: 200,
    }
  );

  const destinationMaterialsListPublicId =
    summaryItemsData?.projectById.materialsList?.publicId;

  const { stockSources } = useGetProjectStockSources(deal?.publicId);

  /**
   * Mutations
   */
  const { mutateAsync: mutateCreateAddress } = useCreateAddress();

  const { mutateAsync: submitDirectPurchaseOrderMutation } =
    useSubmitDirectPurchaseOrderCityPlumbing();

  const { mutateAsync: transferStockMutation } = useTransferStock({
    projectId: deal?.publicId,
  });
  /**
   * Callbacks
   */
  const handleSubmit = async (state: TFormState) => {
    if (state.type === 'stock-transfer') {
      try {
        await transferStockMutation({
          fromMaterialsListId: state.fromMaterialsListId,
          toMaterialsListId: state.toMaterialsListId,
          materials: state.materials.map((material) => ({
            materialId: material.materialsListMaterial.material.id,
            quantity: material.purchaseOrderQuantity,
          })),
        });
        onTransferSuccess?.();
      } catch {
        // The global mutation error handler surfaces a toast and reports it.
      }
      return;
    }

    if (state.type === 'direct-aco' || state.type === 'direct-asap') {
      console.log('Creating direct purchase order', state);
      let deliveryAddressId: number | undefined =
        state.delivery?.deliveryAddressId;
      if (state.delivery?.newDeliveryAddress) {
        const {
          createAddress: { id: addressId },
        } = await mutateCreateAddress({
          line1: state.delivery.newDeliveryAddress.line1,
          line2: state.delivery.newDeliveryAddress.line2,
          city: state.delivery.newDeliveryAddress.city,
          administrativeArea:
            state.delivery.newDeliveryAddress.administrativeArea,
          postalCode: state.delivery.newDeliveryAddress.postcode,
          country: state.delivery.newDeliveryAddress.country,
        });
        if (addressId) {
          deliveryAddressId = +addressId;
        }
      }
      const payload: SubmitDirectPurchaseOrderCityPlumbingInput = {
        materialsListId: materialsListId.toString(),
        materialPurchaseIntents: state.materials.map((material) => ({
          materialsListMaterialId: material.materialsListMaterial.id,
          materialQuantity: material.purchaseOrderQuantity,
        })),
        orderType: deliveryAddressId ? 'DELIVERY' : 'COLLECTION',
        deliveryAddressId: deliveryAddressId?.toString() || undefined,
        branchId:
          state.collection?.branchId.toString() ||
          state.delivery?.branchId?.toString(),
        userContactId: state.userContactId,
        additionalNotes: state.additionalNotes,
        fulfillmentDateType: state.type === 'direct-asap' ? 'ASAP' : 'ACO',
        fromDate:
          (state.type === 'direct-asap'
            ? state.fromDate
            : state.callOffDate
          )?.toISOString() || undefined,
        toDate:
          state.type === 'direct-asap'
            ? state.toDate?.toISOString()
            : undefined,
      };
      if (state.type === 'direct-asap') {
        payload.fulfillmentOptionId = state.fulfillmentOptionId;
      }
      const response = await submitDirectPurchaseOrderMutation(payload);
      if (
        response.submitDirectPurchaseOrderCityPlumbing?.__typename ===
        'SubmitDirectPurchaseOrderCityPlumbingResponseSuccess'
      ) {
        onPurchaseOrderSuccess?.(
          +response.submitDirectPurchaseOrderCityPlumbing.purchaseOrder.id
        );
      } else {
        pushError(
          response.submitDirectPurchaseOrderCityPlumbing.__typename ===
            'SubmitDirectPurchaseOrderCityPlumbingResponseError'
            ? response.submitDirectPurchaseOrderCityPlumbing.message
            : 'Failed to submit purchase order'
        );
      }
    } else {
      console.log('Creating indirect purchase order', state);
      let deliveryAddressId: number | undefined =
        state.deliveryInfo?.deliveryAddressId;
      if (state.deliveryInfo?.newDeliveryAddress) {
        const {
          createAddress: { id: addressId },
        } = await mutateCreateAddress({
          line1: state.deliveryInfo.newDeliveryAddress.line1,
          line2: state.deliveryInfo.newDeliveryAddress.line2,
          city: state.deliveryInfo.newDeliveryAddress.city,
          administrativeArea:
            state.deliveryInfo.newDeliveryAddress.administrativeArea,
          postalCode: state.deliveryInfo.newDeliveryAddress.postcode,
          country: state.deliveryInfo.newDeliveryAddress.country,
        });
        if (addressId) {
          deliveryAddressId = +addressId;
        }
      }
      const requestData: PersistPurchaseOrderRequestData = {
        materialsListId,
        materialPurchaseIntents: state.materials.map((material) => ({
          materialsListMaterialId: +material.materialsListMaterial.id,
          materialQuantity: material.purchaseOrderQuantity,
        })),
        supplierId: state.supplierId,
        dueAt: state.dueAt,
        additionalNotes: state.additionalNotes,
        showMaterialPrices: state.showMaterialPrices,
        deliveryAddressId: deliveryAddressId,
      };
      await new Promise<number>((resolve, reject) => {
        reduxDispatch(
          requestPersistPurchaseOrder(
            requestData,
            (purchaseOrderId) => {
              resolve(purchaseOrderId);
            },
            () => {
              reject(new Error());
            }
          )
        );
      })
        .then((purchaseOrderId) => {
          onPurchaseOrderSuccess?.(purchaseOrderId);
        })
        .catch(() => {
          pushError('Something went wrong, please try again.');
        });
    }
  };

  return (
    <Sidebar
      isOpen={isOpen}
      size="lg"
      // Source-neutral: the wizard can raise a purchase order or move stock in
      // from a stock location, so the heading must not say "purchase order".
      title="Order materials"
      {...rest}
    >
      <Sidebar.Body className="relative overflow-hidden">
        {isLoading || !materialsYetToOrder ? (
          <div className="grid grid-cols-2 h-full">
            <div className="space-y-4 p-4">
              <SkeletonLoader.Title />

              <SkeletonLoader.MaterialCard />
              <SkeletonLoader.MaterialCard />
              <SkeletonLoader.MaterialCard />
              <SkeletonLoader.MaterialCard />
              <SkeletonLoader.MaterialCard />
              <SkeletonLoader.MaterialCard />
              <SkeletonLoader.MaterialCard />
            </div>

            <div className="bg-gray-50"></div>
          </div>
        ) : (
          <CreatePurchaseOrderForm
            projectId={projectId}
            materialListMaterials={materialsYetToOrder.items}
            stockSources={stockSources}
            destinationMaterialsListPublicId={destinationMaterialsListPublicId}
            onSubmit={handleSubmit}
          />
        )}
      </Sidebar.Body>
    </Sidebar>
  );
};

export default CreatePurchaseOrderSidebar;
